/* eslint-disable @typescript-eslint/no-explicit-any */
// Employee-initiated, No-Dues-only separation (deputation / retirement / transfer-out). There is NO resignation approval flow:
// the employee picks the type, date of leaving and a reason, the case is created
// with its No-Dues form enabled, and they are taken straight to the No-Dues form.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  SectionCard,
  FieldLabel,
  ReadOnlyField,
  BottomActionBar,
  WarningBanner,
  useConfirm,
} from "./shared/components";
import {
  getEmployeeDetails,
  getUserCapabilities,
  initiateNoDuesCase,
  formatDate,
  EmployeeDetails,
} from "./services/eSeparation.service";

type CaseType = "DEPUTATION" | "RETIREMENT" | "TRANSFER_OUT";

const CASE_OPTIONS: Array<{
  key: CaseType;
  label: string;
  icon: string;
  accent: string;
  hint: string;
}> = [
  {
    key: "RETIREMENT",
    label: "Retirement",
    icon: "ti-hourglass-high",
    accent: "#4A1A6E",
    hint: "Reason for retirement",
  },
  {
    key: "DEPUTATION",
    label: "Deputation",
    icon: "ti-arrows-left-right",
    accent: "#993C1D",
    hint: "Deputation details — organisation and period",
  },
  {
    key: "TRANSFER_OUT",
    label: "Transfer-Out",
    icon: "ti-arrow-right-circle",
    accent: "#185FA5",
    hint: "Transfer details — destination organisation / location",
  },
];

const NoDuesCaseInitiate: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  const [emp, setEmp] = useState<EmployeeDetails | null>(null);
  const [caseType, setCaseType] = useState<CaseType>("RETIREMENT");
  const [dateOfLeaving, setDateOfLeaving] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [blockReason, setBlockReason] = useState("");

  const activeCase = CASE_OPTIONS.find((c) => c.key === caseType)!;

  useEffect(() => {
    getEmployeeDetails().then((res) => res.data && setEmp(res.data));
    getUserCapabilities().then((c) => {
      if (!c.employee.can_initiate_no_dues_case) {
        const reason =
          c.employee.no_dues_case_block_reason ||
          "A separation request is already in process; you cannot initiate another.";
        setBlockReason(reason);
        toast.info(reason);
        navigate(routes.Esepration.overview, { replace: true });
      }
    });
  }, [navigate, routes]);

  const handleSubmit = async () => {
    if (blockReason) return toast.warning(blockReason);
    if (!dateOfLeaving) return toast.warning("Please select the date of leaving.");
    if (!reason.trim())
      return toast.warning("Please provide a reason.");

    const ok = await confirm(
      `Initiate ${activeCase.label.toLowerCase()} separation? There is no resignation approval flow.`,
      { title: `Confirm ${activeCase.label}`, confirmLabel: "Initiate", tone: "primary" }
    );
    if (!ok) return;

    setSubmitting(true);
    const res = await initiateNoDuesCase({
      case_type: caseType,
      date_of_leaving: dateOfLeaving,
      reason: reason.trim(),
    });
    setSubmitting(false);

    if (!res.success) {
      return toast.error(res.message || "Failed to initiate. Please try again.");
    }
    toast.success("Separation case initiated successfully.");
    navigate(routes.Esepration.overview, { replace: true });
  };

  return (
    <div className="page-wrapper">
      <div className="content pb-0">
        <div className="mb-3">
          <h3 className="page-title mb-1">Initiate No-Dues Separation</h3>
        </div>

        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
          <div className="card-body d-flex align-items-start gap-3">
            <span
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
              style={{ width: 40, height: 40, borderRadius: 10, background: "#E5F0FA", color: "#185FA5" }}
            >
              <i className="ti ti-file-check" style={{ fontSize: 20 }} />
            </span>
            <div>
              <div className="fw-semibold mb-1">No-Dues only</div>
              <small className="text-muted">
                Retirement, deputation and transfer-out follow only
                the No-Dues process — there is no resignation or approval flow. Fill the
                No-Dues form and it goes to HR, who sends it to the departments for clearance.
              </small>
            </div>
          </div>
        </div>

        {blockReason ? (
          <div className="card border-0 shadow-sm p-4 text-center my-4" style={{ borderRadius: 16 }}>
            <div className="mb-3 text-warning">
              <i className="ti ti-alert-triangle" style={{ fontSize: 44 }} />
            </div>
            <h5 className="fw-semibold mb-2">Initiate Separation Unavailable</h5>
            <p className="text-muted mb-4">{blockReason}</p>
            <div>
              <button
                className="btn btn-primary px-4 py-2"
                onClick={() => navigate(routes.Esepration.overview, { replace: true })}
              >
                <i className="ti ti-layout-dashboard me-2" />
                Go to Overview &amp; Status
              </button>
            </div>
          </div>
        ) : (
          <>
            <SectionCard index={0} title="Separation Type">
              <div className="row g-2">
                {CASE_OPTIONS.map((opt) => {
                  const selected = opt.key === caseType;
                  return (
                    <div className="col-6 col-md-4" key={opt.key}>
                      <button
                        type="button"
                        className="btn w-100 d-flex flex-column align-items-center gap-2 py-3"
                        style={{
                          borderRadius: 12,
                          border: `1px solid ${selected ? opt.accent : "rgba(0,0,0,0.12)"}`,
                          background: selected ? `${opt.accent}12` : "#fff",
                        }}
                        onClick={() => setCaseType(opt.key)}
                      >
                        <i
                          className={`ti ${opt.icon}`}
                          style={{ fontSize: 22, color: selected ? opt.accent : "#6c757d" }}
                        />
                        <span
                          className="small text-center"
                          style={{ color: selected ? opt.accent : "#6c757d", fontWeight: selected ? 600 : 400 }}
                        >
                          {opt.label}
                        </span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </SectionCard>

            <SectionCard index={1} title="Employee Details">
              <div className="row g-3">
                <div className="col-md-6">
                  <FieldLabel label="Name of the Employee" />
                  <ReadOnlyField value={emp?.emp_name} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Emp. No." />
                  <ReadOnlyField value={emp?.emp_no} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Designation" />
                  <ReadOnlyField value={emp?.designation} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Regular / Contract" />
                  <ReadOnlyField value={emp?.emp_type} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Deptt. / Place of Posting" />
                  <ReadOnlyField
                    value={[emp?.posting, emp?.place_of_posting].filter(Boolean).join(" / ")}
                  />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Date of Joining DMRC" />
                  <ReadOnlyField value={emp?.doj ? formatDate(emp.doj) : ""} />
                </div>
              </div>
            </SectionCard>

            <SectionCard index={2} title="Case Details">
              <div className="row g-3">
                <div className="col-md-6">
                  <FieldLabel label="Date of Leaving" required />
                  <input
                    type="date"
                    className="form-control"
                    value={dateOfLeaving}
                    onChange={(e) => setDateOfLeaving(e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-3">
                <FieldLabel label={activeCase.hint} required />
                <textarea
                  className="form-control"
                  rows={4}
                  placeholder="Provide the details..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            </SectionCard>
          </>
        )}
      </div>

      {confirmDialog}

      {!blockReason && (
        <BottomActionBar
          primaryLabel={submitting ? "Initiating…" : "Initiate No-Dues Separation"}
          primaryIcon="ti-send"
          secondaryLabel="Cancel"
          onPrimary={handleSubmit}
          onSecondary={() => navigate(routes.Esepration.hub)}
          disabled={submitting || !dateOfLeaving || !reason.trim()}
          note={blockReason}
        />
      )}
    </div>
  );
};

export default NoDuesCaseInitiate;
